import {browserSessionStorage} from "core/browserStorage";
import {create} from "@bufbuild/protobuf";
import {createClient} from "@connectrpc/connect";
import {fireBeacon} from "api/beaconTransport";
import {
	ClientErrorService,
	ReportClientErrorRequest_Locale,
	ReportClientErrorRequest_Surface,
	ReportClientErrorRequestSchema,
} from "gen/kitecorp/transferify/common/v1/client_errors_pb";
import type {Brand} from "vitals/brand";
import {
	CLIENT_ERROR_FRAME_MAX_LENGTH,
	CLIENT_ERROR_MAX_ABSORBED_COUNT,
	CLIENT_ERROR_MAX_FRAMES,
	CLIENT_ERROR_MESSAGE_MAX_LENGTH,
	CLIENT_ERROR_NAME_MAX_LENGTH,
} from "./clientErrorLimits";
import {clientErrorRouteOf, contentErrorRouteOf} from "./clientErrorRoute";

/**
 * What an error boundary reports, and the rules about what it may contain (kitecorp/transferify-ui#2581).
 *
 * ## What this is for
 *
 * When the root boundary renders, the reader sees "This page could not be loaded" and the only record of
 * why is a console line in a browser nobody on the team is sitting at. This module turns the caught value
 * into a bounded record and posts it to the api's first-party ingest
 * (kitecorp/transferify-api-service#2644), through the same anonymous, fire-and-forget transport every
 * other first-party beacon uses.
 *
 * ## What may never be on the wire
 *
 * No session, no account, no cookie, no resolved pathname, no page content, no referrer. The route is a
 * PATTERN (`clientErrorRoute.ts`), the name is cut down to an identifier, and the message and frames are
 * length-bounded by the api's own numbers (`clientErrorLimits.ts`).
 *
 * **One thing is removed here, and only one: the page's own origin.** A V8 frame and a chunk-load message
 * carry the full script URL, and on a claimed subdomain (`acme.transferify.ro`) or a Premium custom domain
 * that host names the customer whose page broke. Only the browser knows its own origin exactly, so it is
 * taken out of the message and every frame before bounding (the path and the chunk name stay); the `brand`
 * field already says which market it was. Everything else is the api's: it removes e-mail addresses and
 * token-shaped runs before anything is written down (`ClientErrorText`), and that is the one place that
 * general rule lives, because two copies of a redaction grammar drift and on a privacy rule that means one
 * of them silently stops removing something. What this module also owns is the BOUNDS, which the contract
 * test pins to the api's.
 *
 * ## Why it is deduplicated per tab
 *
 * A boundary can re-render, and the guard counts absorptions for as long as the page lives. Without a
 * gate, one broken render loop would post until the abuse budget was spent — and the budget is what every
 * OTHER reader's report needs. One report per key per TAB, held in `sessionStorage`, which survives a
 * reload in the same tab: a re-render does not report again, and neither does a reload loop (a stale chunk
 * that fails on every load), while the same failure in a new tab does.
 *
 * React-free by mandate (`logic-outside-react`): the boundaries are views and decide only when to call in.
 */

/** Which part of the app caught it. The api's `ClientErrorSurface` constants, verbatim. */
export type ClientErrorSurface = "ROOT_BOUNDARY" | "CONTENT_BOUNDARY" | "TRANSLATED_DOM_GUARD";

/** The api's `ClientErrorLocale` constants, verbatim: the upper-case form. */
export type ClientErrorLocaleTag = "EN" | "RO" | "DE";

/** One report's complete body, exactly as the api takes it. */
export interface ClientErrorReport {
	/** The brand KEY (`transferify.ro`), the spelling the vitals beacon sends; the api writes the key. */
	brand: Brand;
	surface: ClientErrorSurface;
	locale: ClientErrorLocaleTag;
	route: string;
	name: string;
	message: string;
	frames: string[];
	absorbedCount?: number;
}

/** What a caller knows about the thing that went wrong. */
export interface ClientErrorInput {
	surface: ClientErrorSurface;
	/** The caught value, of any shape: a boundary catches whatever was thrown. */
	error: unknown;
	brand: Brand;
	/** The active locale as `i18n/localePreference` resolves it (`"en"`, `"ro"`, `"de"`). */
	locale: string;
	/** `window.location.pathname`; only its PATTERN reaches the wire. */
	pathname: string;
	/** `window.location.origin`, removed from the message and the frames before anything is sent. */
	origin: string;
	/** The translated-DOM guard's absorption count. Absent on every other surface. */
	absorbedCount?: number;
}

/**
 * The name for a value that is not an `Error`, or whose own name survives nothing.
 *
 * The api admits only an identifier in that field, so a name has to be reduced to one here or the report is
 * refused — and a refused report is the failure this whole feature exists to stop being silent.
 */
export const CLIENT_ERROR_FALLBACK_NAME = "UnknownError";

/** Namespaced so a key here can never collide with another feature's session entry. */
const SENT_KEY_PREFIX = "transferify.clientError.sent.";

/**
 * The frame lines of a stack, with the leading message line dropped.
 *
 * Both engine formats are handled by taking LINES rather than by matching `at`: V8 writes
 * `TypeError: msg\n    at fn (url:1:2)`, and Firefox writes `fn@url:1:2` with no `at` at all — so an
 * `at`-anchored filter would report zero frames on Firefox and nobody would know.
 */
function frameLinesOf(stack: string): string[] {
	return stack
		.split("\n")
		.slice(1)
		.map((line) => line.trim())
		.filter((line) => line !== "");
}

/** The identifier the api will accept, out of whatever the thrown value called itself. */
function identifierNameOf(name: string): string {
	const identifier = name.replace(/[^A-Za-z0-9_$]/g, "");
	return /^[A-Za-z_$]/.test(identifier)
		? identifier.slice(0, CLIENT_ERROR_NAME_MAX_LENGTH)
		: CLIENT_ERROR_FALLBACK_NAME;
}

/** A string cut to `max`, with no mark: the api appends its own when IT truncates. */
function bounded(value: string, max: number): string {
	return value.length <= max ? value : value.slice(0, max);
}

/** What the page's own host is replaced with where it appears without its scheme. */
const OWN_HOST_MARK = "<host>";

/**
 * `text` without the page's own origin, and without its host where that appears bare: a script URL keeps
 * its path (`/assets/chunk-hash.js:5:1234`), which is what says where it broke, and loses the host, which
 * on a claimed subdomain or a custom domain says whose page it was.
 */
function withoutOwnOrigin(text: string, origin: string): string {
	if (origin === "") {
		return text;
	}
	let host: string;
	try {
		host = new URL(origin).host;
	} catch {
		return text;
	}
	return text.split(origin).join("").split(host).join(OWN_HOST_MARK);
}

/**
 * The name, message and first frames of a caught value of any shape.
 *
 * @param origin the page's own origin, removed from the message and every frame before bounding; `""`
 *   removes nothing
 */
export function describeCaughtError(
	error: unknown,
	origin: string,
): Pick<ClientErrorReport, "name" | "message" | "frames"> {
	if (error instanceof Error) {
		return {
			name: identifierNameOf(error.name),
			// A message is required by the api, so an Error with an empty one still has to say something;
			// its own name is the most honest thing available.
			message: bounded(withoutOwnOrigin(error.message === "" ? error.name : error.message, origin),
				CLIENT_ERROR_MESSAGE_MAX_LENGTH),
			frames: frameLinesOf(error.stack ?? "")
				.slice(0, CLIENT_ERROR_MAX_FRAMES)
				.map((frame) => bounded(withoutOwnOrigin(frame, origin), CLIENT_ERROR_FRAME_MAX_LENGTH)),
		};
	}
	// A thrown non-Error has no name and no stack. `String(error)` is bounded like any message and goes
	// through the api's redactor like any other, so a thrown object carrying data is handled there.
	return {
		name: CLIENT_ERROR_FALLBACK_NAME,
		message: bounded(withoutOwnOrigin(stringify(error), origin), CLIENT_ERROR_MESSAGE_MAX_LENGTH),
		frames: [],
	};
}

/** `String(value)` that cannot itself throw — a thrown object may have a hostile `toString`. */
function stringify(value: unknown): string {
	try {
		const text = String(value);
		return text === "" ? CLIENT_ERROR_FALLBACK_NAME : text;
	} catch {
		return CLIENT_ERROR_FALLBACK_NAME;
	}
}

/** The locale tag the api takes, for any locale string. Anything unrecognised, French included until kitecorp/contracts#77 adds LOCALE_FR, reports as English. */
function localeTagOf(locale: string): ClientErrorLocaleTag {
	const upper = locale.toUpperCase();
	return upper === "RO" || upper === "DE" ? upper : "EN";
}

/** The body to post for one caught error. Pure: the caller decides whether to send it. */
export function clientErrorReport(input: ClientErrorInput): ClientErrorReport {
	const described = describeCaughtError(input.error, input.origin);
	const report: ClientErrorReport = {
		brand: input.brand,
		surface: input.surface,
		locale: localeTagOf(input.locale),
		// The content boundary renders only for a corpus page, which the route table cannot list.
		route: input.surface === "CONTENT_BOUNDARY"
			? contentErrorRouteOf(input.pathname)
			: clientErrorRouteOf(input.pathname),
		...described,
	};
	if (input.absorbedCount !== undefined) {
		// Clamped rather than dropped: the api refuses anything past its ceiling, and a report saying "at
		// least this many" is worth more than no report. Zero is not a report — the api refuses it — so a
		// guard that absorbed nothing has nothing to say and the field is left off.
		const clamped = Math.min(Math.trunc(input.absorbedCount), CLIENT_ERROR_MAX_ABSORBED_COUNT);
		if (clamped > 0) {
			report.absorbedCount = clamped;
		}
	}
	return report;
}

/**
 * The session key a report is deduplicated on.
 *
 * The guard's key is its SURFACE alone, because ui#2581 asks for one absorption report per tab however
 * many it absorbed and wherever the reader went. A boundary's key carries the route and the error name
 * too: a second, different failure on another page is news, and a boundary re-rendering with the same
 * error is not.
 */
export function clientErrorDedupeKey(report: ClientErrorReport): string {
	return report.surface === "TRANSLATED_DOM_GUARD"
		? report.surface
		: `${report.surface}|${report.route}|${report.name}`;
}

/**
 * Whether this key has not been reported in this tab yet, marking it when it has not.
 *
 * With no session storage at all — a privacy-locked browser, the SSG prerender — this answers `true` every
 * time rather than refusing to report: the abuse budget is the backstop, and silence is the outcome this
 * feature exists to remove.
 */
function claimFirstReportOfSession(key: string): boolean {
	const storage = browserSessionStorage();
	if (storage === null) {
		return true;
	}
	const entry = SENT_KEY_PREFIX + key;
	try {
		if (storage.getItem(entry) !== null) {
			return false;
		}
		storage.setItem(entry, "1");
		return true;
	} catch {
		// A quota or privacy failure must not become an exception inside an error boundary, which is the
		// one place in the app that has nowhere left to throw to.
		return true;
	}
}

/**
 * The `ReportClientError` request for one report. The surface and the locale are the generated enum member of
 * the same name, indexed by the string the report holds, so `tsc` refuses a name the contract does not have.
 */
function clientErrorRequest(report: ClientErrorReport) {
	return create(ReportClientErrorRequestSchema, {
		...report,
		surface: ReportClientErrorRequest_Surface[report.surface],
		locale: ReportClientErrorRequest_Locale[report.locale],
	});
}

/**
 * Reports one caught error, and answers whether this call put a request on the wire.
 *
 * `false` means either that this tab already reported the same thing, or that the browser had no `fetch` to
 * send with (a runtime without one). Never throws: the callers are error boundaries and a DOM patch.
 */
export function reportClientError(input: ClientErrorInput): boolean {
	try {
		const report = clientErrorReport(input);
		if (!claimFirstReportOfSession(clientErrorDedupeKey(report))) {
			return false;
		}
		return fireBeacon(transport => createClient(ClientErrorService, transport).reportClientError(
			clientErrorRequest(report)));
	} catch {
		return false;
	}
}

/**
 * The brand resolver, loaded LAZILY and once.
 *
 * `vitals/brand` holds the brand vocabulary, and `scripts/vitals-lazy-sweep.mjs` keeps the vitals modules
 * out of the eager graph: that graph is what a visitor downloads before anything renders, and ui#1554
 * measured what adding to it costs LCP. A static import here would have put it there
 * through two eager paths at once — `routes.tsx` imports both boundaries for their `errorElement`, and
 * `main.tsx` imports the translated-DOM guard — which is exactly what the sweep caught on this change.
 *
 * Lazy costs nothing on either path. A boundary only renders once the app has already failed and the
 * reader is looking at an error page, and the guard reports on `pagehide`. It is also the better default:
 * a reader whose app never breaks never fetches this at all.
 */
let brandModule: Promise<typeof import("vitals/brand")> | null = null;

function loadBrand(): Promise<typeof import("vitals/brand")> {
	brandModule ??= import("vitals/brand");
	return brandModule;
}

/**
 * Starts loading what {@link reportCaughtError} needs, without reporting anything.
 *
 * The translated-DOM guard calls this on its FIRST absorption, because its own report happens at
 * `pagehide` — the last moment a page has — and a module fetch started there would usually not finish.
 * An absorption happens long before that, so by the time the count is final the resolver is in memory.
 */
export function prepareCaughtErrorReporting(): void {
	void loadBrand().catch(() => {
		// A chunk that cannot be fetched means no report. There is nowhere to raise that to: the callers
		// are an error boundary and a DOM patch.
	});
}

/**
 * Reports a caught error, resolving the brand from the current host.
 *
 * Asynchronous only because of the lazy resolver above; it is never awaited by a caller and never throws.
 * Answers whether a request left this browser, so a test can assert the dedupe rather than infer it.
 */
export async function reportCaughtError(input: Omit<ClientErrorInput, "brand" | "origin">): Promise<boolean> {
	try {
		const {brandOf} = await loadBrand();
		return reportClientError({...input, brand: brandOf(window.location.hostname), origin: window.location.origin});
	} catch {
		return false;
	}
}
